import { Injectable, Signal, inject, signal } from '@angular/core';
import { SelectedScope } from '../shared/app-filters/app-filters.component';
import { OrganisationHierarchyService, scopeKey } from '../shared/organisation-hierarchy.service';
import { HostAppContext } from './host-application.interfaces';

/**
 * What the user is currently looking at, shaped for the `enterprise_insights.unityaiot.com`
 * section of the assistant's context. The snake_case names are the wire contract read by the
 * server, so they deliberately differ from the camelCase used elsewhere in the SPA.
 */
export interface EnterpriseInsightsContext {
  insight_occurrence_id: string | null;
  company_ids: string[];
  organisation_ids: string[];
  site_ids: string[];
  facility_ids: string[];
  active_tab: string | null;
  visible_tabs: string[];
}

/** The organisation or facility of the card whose detail page is open, for the matching host section. */
export type ItemInView =
  | { platform: 'on-road'; orgId: string; orgName: string }
  | { platform: 'on-site'; facilityId: string; facilityName: string };

/**
 * Overlays the item in view onto the host context, without mutating it.
 *
 * A host section survives only when the item matches its platform: `on_road.unityaiot.com` for an
 * on-road item, `on_site.unityaiot.com` for an on-site item, and neither when there is no item.
 * The surviving section is rewritten with the item, and a section the host did not send is never
 * added, so the host keeps ownership of which sections exist.
 *
 * @param host The context supplied by the embedding host, if any.
 * @param item The item in view, or null when none is open.
 * @returns The host context with the item applied, or undefined when there is no host context.
 */
export function withItemInView(
  host: HostAppContext | undefined,
  item: ItemInView | null,
): HostAppContext | undefined {
  if (!host) {
    return host;
  }

  const {
    'on_road.unityaiot.com': onRoad,
    'on_site.unityaiot.com': onSite,
    ...rest
  } = host;

  if (item?.platform === 'on-site' && onSite) {
    return {
      ...rest,
      'on_site.unityaiot.com': {
        ...onSite,
        facility_id: item.facilityId,
        facility_name: item.facilityName,
      },
    };
  }

  if (item?.platform === 'on-road' && onRoad) {
    return {
      ...rest,
      'on_road.unityaiot.com': { ...onRoad, org_id: item.orgId, org_name: item.orgName },
    };
  }

  return rest;
}

/**
 * Compares two identifier lists by their contents, ignoring order.
 *
 * @param a First list.
 * @param b Second list.
 * @returns True when both lists hold the same identifiers, however they are ordered.
 */
function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Holds the page context the Aura chat panel sends alongside each message, so routed components
 * can publish what the user is looking at without knowing about the chat runtime.
 *
 * Both setters keep the existing object when nothing actually changed: the runtime re-fetches
 * suggested prompts and knowledge-base tags whenever the context changes identity, and an
 * unrelated filter change should not cost two HTTP calls.
 */
@Injectable({ providedIn: 'root' })
export class AuraPageContextService {
  private readonly context = signal<EnterpriseInsightsContext>({
    insight_occurrence_id: null,
    company_ids: [],
    organisation_ids: [],
    site_ids: [],
    facility_ids: [],
    active_tab: null,
    visible_tabs: [],
  });

  private readonly hierarchy = inject(OrganisationHierarchyService);
  private readonly item = signal<ItemInView | null>(null);

  /** The organisation or facility of the open card, or null when none resolves. */
  public readonly itemInView: Signal<ItemInView | null> = this.item.asReadonly();

  /** The context as it stands now. */
  public readonly pageContext: Signal<EnterpriseInsightsContext> = this.context.asReadonly();

  /**
   * Publishes the insight occurrence the user has open, leaving the scope lists untouched.
   *
   * @param id Identifier of the open occurrence, or null when none is open.
   */
  public setInsightOccurrenceId(id: string | null): void {
    const current = this.context();
    if (current.insight_occurrence_id === id) {
      return;
    }

    this.context.set({ ...current, insight_occurrence_id: id });
  }

  /**
   * Publishes the selected organisational scope, leaving the occurrence id untouched.
   *
   * Equality is judged on list contents because the filter component emits freshly built arrays
   * on every selection, so comparing references would never report a match.
   *
   * @param scope The scope currently selected in the filter bar.
   */
  public setScope(scope: SelectedScope): void {
    const current = this.context();
    const companyIds = scope.companyId ? [scope.companyId] : [];

    if (
      sameIds(current.company_ids, companyIds) &&
      sameIds(current.organisation_ids, scope.organisationIds) &&
      sameIds(current.site_ids, scope.siteIds) &&
      sameIds(current.facility_ids, scope.facilityIds)
    ) {
      return;
    }

    this.context.set({
      ...current,
      company_ids: companyIds,
      organisation_ids: [...scope.organisationIds],
      site_ids: [...scope.siteIds],
      facility_ids: [...scope.facilityIds],
    });
  }

  /**
   * Publishes the active tab and the tabs currently visible to the user for the open insight.
   *
   * Both are set together because they always change from the same source (the review page's
   * own tab state), and comparing `visibleTabs` by contents - not reference - matters for the
   * same reason it does in `setScope`: a fresh array is rebuilt on every emission.
   *
   * @param activeTab Label of the tab the user currently has open, or null when no insight
   *   detail view is active.
   * @param visibleTabs Labels of every tab currently offered to the user, in any order.
   */
  public setInsightView(activeTab: string | null, visibleTabs: string[]): void {
    const current = this.context();
    if (current.active_tab === activeTab && sameIds(current.visible_tabs, visibleTabs)) {
      return;
    }

    this.context.set({ ...current, active_tab: activeTab, visible_tabs: [...visibleTabs] });
  }

  /**
   * Clears the item in view, for when the card detail page is left. Does nothing when none is set.
   */
  public clearItemInView(): void {
    this.setItem(null);
  }

  /**
   * Resolves the card's scope entity to the item the host section should carry, and publishes it.
   * Only On-Road organisation and site cards and On-Site facility cards are accepted; any other
   * pair is a normal state and sets null silently. An accepted pair that cannot be resolved sets
   * null and is logged.
   *
   * @param platform The card's platform, as the API reports it, or null when absent.
   * @param scopeEntityType The card's scope entity type, in any letter case, or null when absent.
   * @param scopeEntityId The card's scope entity id, or null when absent.
   */
  public setItemInView(
    platform: string | null | undefined,
    scopeEntityType: string | null | undefined,
    scopeEntityId: string | null | undefined,
  ): void {
    if (platform !== 'on-road' && platform !== 'on-site') {
      this.setItem(null);
      return;
    }

    // A missing type is an unresolvable card, so it falls through to the log below.
    const type = scopeEntityType?.toLowerCase();
    const accepted =
      platform === 'on-site' ? type === 'facility' : type === 'organisation' || type === 'site';
    if (type && !accepted) {
      this.setItem(null);
      return;
    }

    // One-off read of the hierarchy index: it relies on the hierarchy being loaded before a card can
    // be opened (the filters load it on the landing page, the only route to a detail page).
    const node =
      scopeEntityType && scopeEntityId
        ? this.hierarchy.scopeIndex().get(scopeKey(scopeEntityType, scopeEntityId))
        : undefined;
    const entity =
      platform === 'on-site'
        ? node
          ? { id: scopeEntityId, name: node.name }
          : undefined
        : node?.owningOrganisation;
    if (!entity?.id || !entity.name) {
      console.error(
        'Cannot resolve the item in view for platform, entity type and id:',
        platform,
        scopeEntityType,
        scopeEntityId,
      );
      this.setItem(null);
      return;
    }

    this.setItem(
      platform === 'on-site'
        ? { platform, facilityId: entity.id, facilityName: entity.name }
        : { platform, orgId: entity.id, orgName: entity.name },
    );
  }

  /**
   * Writes the item only when it differs from the current one, so an equal value keeps the
   * signal, and therefore the chat context, unchanged. Two items are equal when the platform and
   * both identifier fields match.
   *
   * @param next The item to publish, or null for none.
   */
  private setItem(next: ItemInView | null): void {
    const current = this.item();
    // Same platform means the same keys, so comparing the current entries is enough.
    const equal =
      current === next ||
      (current !== null &&
        next !== null &&
        Object.entries(current).every(([key, value]) => (next as Record<string, string>)[key] === value));
    if (!equal) {
      this.item.set(next);
    }
  }
}
